import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  SyntheticEvent,
} from "react";
import { StyleSettings } from "src/styles/StyleSettings";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { IconButton } from "@mui/material";
import SnGrid from "@shared/components/Grid/SnGrid";
import { getTablebySql } from "@shared/utils/CommonUtils";
import { Box, TextField, Typography } from "@mui/material";
import SnButton from "@shared/components/SnButton";
import SnMemoBox from "@shared/components/SnMemoBox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import SnTab from "@shared/components/SnTab";
import SearchPanel from "@widgets/SearchPanel";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";

import Editor from "@monaco-editor/react";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Props {
  query: string;
  data: any;
  cdMenu?: string;
  modalRef?: any; // 부모 엘리먼트
}

const DesignerQueryEdit = forwardRef(
  ({ data, cdMenu, modalRef, query = "" }: Props, ref) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const [querySave, setQuerySave] = useState<string>("");
    const [queryExec, setQueryExec] = useState<string>("");

    const [addtitles, setAddTitles] = useState<string[]>([]);
    const [gridDatas, setGridDatas] = useState<any[]>([]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          if (query == "") {
            const params = {
              queryId: "getQuery",
              id: "DesignerMapper",
              params: {
                cd_query: cdMenu?.toLowerCase() + "_01",
              },
            };
            const rtn: any = await fnGetDataSet(params);
            if (rtn[0].length > 0) {
              const query = rtn[0][0].dc_query;
              setQuerySave(query);
              const queryParams = data.formData;
              const response = await axios.post(
                "/snotes/DESIGNER_REPLACEQUERY",
                { sql: query, queryParams: queryParams },
                {
                  headers: {
                    "X-TENANT-ID":
                      global.getState().userInfo.tenant_id || "DEFAULT",
                  },
                }
              );

              const result = response.data;
              setQueryExec(result);
            }
          } else {
            setQuerySave(query);
            const queryParams = data.formData;
            const response = await axios.post(
              "/snotes/DESIGNER_REPLACEQUERY",
              { sql: query, queryParams: queryParams },
              {
                headers: {
                  "X-TENANT-ID":
                    global.getState().userInfo.tenant_id || "DEFAULT",
                },
              }
            );

            const result = response.data;
            setQueryExec(result);
          }
        } catch (error) {}
      };
      fetchform();
    }, [data]);

    useImperativeHandle(ref, () => ({
      getSelectedRow: () => {
        return [querySave];
      },
    }));

    const onChange = async () => {
      const queryParams = searchRef.current.getFormdata();
      const response = await axios.post(
        "/snotes/DESIGNER_REPLACEQUERY",
        { sql: querySave, queryParams: queryParams },
        {
          headers: {
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        }
      );

      const result = response.data;
      setQueryExec(result);
    };

    const onSearch = async () => {
      const queryParams = searchRef.current.getFormdata();
      const response = await axios.post(
        "/snotes/DESIGNER_SEARCH",
        { sql: querySave, queryParams: queryParams },
        {
          headers: {
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        }
      );

      const result: any[] = response.data;
      if (result && result.length > 0) {
        const newTitles = result.map((_, index) => (index + 1).toString());

        setAddTitles(newTitles);
        setGridDatas(result);
      } else {
        setAddTitles([]);
        setGridDatas([]);
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div>
          <SearchPanel
            ref={searchRef}
            data={data.formData}
            controls={data.formControlData}
          ></SearchPanel>
        </div>
        <div className="flex-1">
          <SnTab
            titles={["저장쿼리", "실행쿼리", ...addtitles]}
            width={"100%"}
            body={[
              <Editor
                height="500px"
                language="sql"
                value={querySave}
                onChange={(value) => setQuerySave(value || "")}
                onMount={(editor) => {
                  // 직접 Observer를 생성하여 제어
                  const observer = new ResizeObserver(() => {
                    window.requestAnimationFrame(() => {
                      editor.layout();
                    });
                  });

                  // 에디터가 담긴 DOM 요소를 감시
                  const container = editor.getDomNode()?.parentElement;
                  if (container) observer.observe(container);

                  return () => observer.disconnect(); // 언마운트 시 정리
                }}
                options={{
                  fontSize: 14,
                  minimap: { enabled: false },
                  folding: true,
                  lineNumbers: "on",
                  scrollBeyondLastLine: false,
                  automaticLayout: true, // 이 옵션과 layout() 호출이 만나면 안정적입니다.
                  wordWrap: "on",
                  theme: "vs-light",
                }}
              />,
              <Editor
                height="500px"
                language="sql"
                value={queryExec}
                onChange={(value) => setQueryExec(value || "")}
                onMount={(editor) => {
                  // 직접 Observer를 생성하여 제어
                  const observer = new ResizeObserver(() => {
                    window.requestAnimationFrame(() => {
                      editor.layout();
                    });
                  });

                  // 에디터가 담긴 DOM 요소를 감시
                  const container = editor.getDomNode()?.parentElement;
                  if (container) observer.observe(container);

                  return () => observer.disconnect(); // 언마운트 시 정리
                }}
                options={{
                  fontSize: 14,
                  minimap: { enabled: false },
                  folding: true,
                  lineNumbers: "on",
                  scrollBeyondLastLine: false,
                  automaticLayout: true, // 이 옵션과 layout() 호출이 만나면 안정적입니다.
                  wordWrap: "on",
                  theme: "vs-light",
                }}
              />,
              ...addtitles.map((title, index) => {
                const rowData = gridDatas[index] || [];

                // 1. 행 번호 컬럼 정의 (맨 앞에 위치)
                const rowNumberCol = {
                  headerName: "No.",
                  valueGetter: "node.rowIndex + 1",
                  width: 70,
                  pinned: "left" as const, // 이 부분을 수정하세요
                  sortable: false,
                  filter: false,
                  suppressMovable: true,
                };

                // 2. 데이터에서 추출한 컬럼들
                const dataColumns =
                  rowData.length > 0
                    ? Object.keys(rowData[0]).map((key) => ({
                        field: key,
                        headerName: key.toUpperCase(),
                        minWidth: 100,
                      }))
                    : [];

                // 3. 두 컬럼 배열 합치기
                const finalColumnDefs = [rowNumberCol, ...dataColumns];

                return (
                  <div
                    key={index}
                    className="ag-theme-alpine"
                    style={{
                      height: "100%",
                      width: "100%",
                      marginBottom: "20px",
                    }}
                  >
                    <AgGridReact
                      rowData={rowData}
                      columnDefs={finalColumnDefs} // 행 번호가 포함된 컬럼 설정
                      onFirstDataRendered={(params) => {
                        params.api.autoSizeAllColumns();
                      }}
                      defaultColDef={{
                        resizable: true,
                      }}
                    />
                  </div>
                );
              }),
            ]}
            header={[
              <div></div>,
              <div>
                <SnButton text="조회" onClick={onSearch}></SnButton>
                <SnButton text="변환" onClick={onChange}></SnButton>
              </div>,
              ...addtitles.map((title, index) => <div></div>),
            ]}
          />
        </div>
      </div>
    );
  }
);

export default DesignerQueryEdit;
